import React from "react";
import { useLeaderboard, LeaderboardEntry } from "../hooks/useLeaderboard";
import { useLazyDirtyRefreshOnFocus } from "../hooks/useLazyDirtyRefreshOnFocus";
import { StatusMessage } from "../components/ui";
import { cn } from "../lib/utils";
import { Crown } from "lucide-react";
import { useLocalization } from "../lib/i18n";
import { formatPnlOneDecimal } from "@sportex100/shared/utils/pnlFormatting";

type StandingsPageProps = {
  poolCode: string | null;
  isActive: boolean;
  currentTraderCode?: string | null;
};

const numberFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});

const formatValue = (value: number): string => {
  const formatted = numberFormatter.format(Math.abs(value));
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `-${formatted}`;
  return formatted;
};

const hasNonZeroPnl = (value: number) => Math.abs(value) > 0.0001;

const formatFreshness = (refreshedAt?: string | null): string | null => {
  if (!refreshedAt) return null;
  const refreshedDate = new Date(refreshedAt);
  if (Number.isNaN(refreshedDate.getTime())) return null;
  return `Updated ${refreshedDate.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  })}`;
};

export const StandingsPage: React.FC<StandingsPageProps> = ({
  poolCode,
  isActive,
  currentTraderCode = null,
}) => {
  const { t } = useLocalization();
  const { data, isLoading, isError, reload } = useLeaderboard({
    poolCode,
    enabled: !!poolCode,
  });

  useLazyDirtyRefreshOnFocus("leaderboard", poolCode, isActive, async () => {
    const result = await reload();
    return result;
  });

  if (isLoading) {
    return (
      <section className="rounded-card border border-border-subtle bg-background-card p-3">
        <StatusMessage>{t("home.feed.loading")}</StatusMessage>
      </section>
    );
  }

  if (isError) {
    return (
      <section className="rounded-card border border-border-subtle bg-background-card p-3">
        <StatusMessage variant="error">{t("home.feed.error")}</StatusMessage>
      </section>
    );
  }

  if (!data || data.leaderboard.length === 0) {
    return (
      <section className="rounded-card border border-border-subtle bg-background-card p-3">
        <StatusMessage>{t("standings.empty.title")}</StatusMessage>
      </section>
    );
  }

  const getTraderDisplayName = (entry: LeaderboardEntry) =>
    !entry.trader_code && entry.display_name?.trim().toLowerCase() === "deleted trader"
      ? t("standings.deletedTrader")
      : entry.trader_code || entry.display_name?.trim() || t("standings.deletedTrader");

  const getLeaderboardEntryKey = (entry: LeaderboardEntry) => {
    const code = entry.trader_code?.trim();
    if (code) return `code:${code}`;
    return `deleted:${entry.rank ?? "unranked"}:${getTraderDisplayName(entry)}:${entry.trade_count}:${entry.total_value}`;
  };

  const rankedEntries = data.leaderboard.filter((entry) => entry.rank !== null);
  const topThreeEntries = rankedEntries.slice(0, 3);
  const freshnessLabel = formatFreshness(data.refreshed_at);
  const topThreeCodes = new Set(topThreeEntries.map((entry) => getLeaderboardEntryKey(entry)));
  const fieldEntries =
    topThreeEntries.length > 0
      ? data.leaderboard.filter((entry) => !topThreeCodes.has(getLeaderboardEntryKey(entry)))
      : data.leaderboard;

  const getTraderInitials = (displayName: string) => {
    const trimmed = displayName.trim();
    if (!trimmed) return "--";
    return trimmed.slice(0, 2).toUpperCase();
  };

  const renderTraderAvatar = (entry: LeaderboardEntry, sizeClass: string) => {
    const displayName = getTraderDisplayName(entry);
    const initials = getTraderInitials(displayName);
    if (entry.avatar_url) {
      return (
        <img
          src={entry.avatar_url}
          alt={`${displayName} avatar`}
          className={cn("rounded-full border border-border-subtle/60 object-cover", sizeClass)}
          loading="lazy"
          decoding="async"
        />
      );
    }
    return (
      <div className={cn(
        "flex items-center justify-center rounded-full border border-border-subtle/60 bg-background-raised font-bold text-foreground",
        sizeClass,
      )}>
        {initials}
      </div>
    );
  };

  const isCurrentTraderEntry = (entry: LeaderboardEntry) =>
    !!currentTraderCode &&
    !!entry.trader_code &&
    entry.trader_code.trim().toUpperCase() === currentTraderCode.trim().toUpperCase();

  const getPnlClassName = (entry: LeaderboardEntry) => {
    if (!hasNonZeroPnl(entry.total_pnl) && entry.trade_count === 0) {
      return "text-foreground-muted";
    }
    if (entry.total_pnl > 0) return "text-success";
    if (entry.total_pnl < 0) return "text-danger";
    return "text-foreground-muted";
  };

  const shouldShowPnl = (entry: LeaderboardEntry) =>
    entry.trade_count > 0 || hasNonZeroPnl(entry.total_pnl);

  return (
    <section className="space-y-3">
      {topThreeEntries.length > 0 && (
        <section className="border-b border-border-subtle/40 px-3 py-3 md:px-4">
          <div className="mx-auto w-full max-w-[56rem]">
            <div className="mb-2 flex items-center justify-between">
              <h4 className="font-display text-2xl font-bold uppercase leading-none text-foreground sm:text-body sm:font-semibold sm:tracking-[0.12em]">{t("standings.podium.title")}</h4>
              <p className="font-display text-sm uppercase leading-none text-foreground-muted sm:text-body-xs sm:tracking-[0.12em]">
                {freshnessLabel ?? t("standings.podium.subtitle")}
              </p>
            </div>
            <div className="rounded-[18px] border border-border-subtle/50 bg-background-surface/40 p-2 md:p-3">
              <div className="grid grid-cols-3 items-end gap-2 md:gap-3">
                {[topThreeEntries[1], topThreeEntries[0], topThreeEntries[2]].map((entry, index) => {
                  if (!entry) return <div key={`podium-empty-${index}`} />;
                  const isLeader = entry.rank === 1;
                  const isSecond = entry.rank === 2;
                  const isThird = entry.rank === 3;
                  return (
                    <article
                      key={getLeaderboardEntryKey(entry)}
                      className={cn(
                        "relative overflow-hidden rounded-[18px] border bg-background px-2 py-2 md:px-3 md:py-3",
                        isLeader
                          ? "border-warning/60 bg-warning/10"
                          : isSecond
                            ? "border-slate-400/45 bg-slate-400/10"
                            : "border-amber-700/45 bg-amber-700/10",
                        isLeader ? "md:min-h-[200px]" : "md:min-h-[168px]",
                      )}
                    >
                      <div className="absolute right-2 top-2 rounded-md border border-border-subtle/50 bg-background/80 px-1.5 py-0.5 text-sm font-semibold text-foreground">
                        {entry.rank}
                      </div>
                      {isLeader ? (
                        <div className="mb-1 flex justify-center text-warning">
                          <Crown className="h-5 w-5" />
                        </div>
                      ) : null}
                      <div className="standingsTabletPodiumAvatar mx-auto mb-2 h-11 w-11 md:h-14 md:w-14">
                        {renderTraderAvatar(entry, "h-full w-full text-sm md:text-lg")}
                      </div>
                      <p className="standingsTabletPodiumTraderCode truncate text-center font-display text-lg font-bold uppercase leading-tight text-foreground md:text-xl">
                        {getTraderDisplayName(entry)}
                      </p>
                      <div className="mt-2 border-t border-border-subtle/40 pt-2 text-center">
                        <p className="font-display text-[10px] uppercase leading-none text-foreground-muted md:text-[11px] md:tracking-[0.1em]">{t("standings.table.pnl")}</p>
                        <p className={cn(
                          "font-mono text-lg font-bold tabular-nums md:text-4xl",
                          getPnlClassName(entry),
                        )}>
                          {shouldShowPnl(entry) ? formatPnlOneDecimal(entry.total_pnl) : "-"}
                        </p>
                        <p className="font-mono text-xs text-foreground-muted md:text-sm">
                          {entry.trade_count} {entry.trade_count === 1 ? t("standings.trade.one") : t("standings.trade.many")}
                        </p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Desktop Table View */}
      <div className="hidden overflow-x-auto">
        <table className="w-full text-left text-sm md:text-base leading-tight">
          <thead className="border-b border-border-subtle/40 bg-background/40 text-[10px] md:text-xs uppercase tracking-wider text-foreground-muted">
            <tr>
              <th className="w-14 md:w-20 px-2 md:px-3 py-1.5 text-left border-r border-border-subtle/20">{t("standings.table.rank")}</th>
              <th className="w-24 md:w-32 px-2 md:px-3 py-1.5 text-left border-r border-border-subtle/20">{t("standings.table.trader")}</th>
              <th className="px-2 md:px-3 py-1.5 text-right border-r border-border-subtle/20">{t("portfolio.summary.trading")}</th>
              <th className="px-2 md:px-3 py-1.5 text-right border-r border-border-subtle/20">{t("portfolio.position.realized")}</th>
              <th className="hidden md:table-cell px-3 py-1.5 text-right border-r border-border-subtle/20">{t("standings.table.trades")}</th>
              <th className="px-2 md:px-3 py-1.5 text-right">{t("portfolio.metric.total")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle/20">
            {fieldEntries.map((entry: LeaderboardEntry, index: number) => {
              const isPassive = entry.rank === null;

              return (
                <tr
                  key={getLeaderboardEntryKey(entry)}
                  className={cn(
                    "transition-all hover:bg-brand/5",
                    index % 2 === 0 ? "bg-background/40" : "bg-background-surface/30",
                    isPassive && "opacity-60"
                  )}
                >
                  <td className="w-14 md:w-20 px-2 md:px-3 py-1.5 border-r border-border-subtle/20">
                    <span className="text-base md:text-xl font-semibold text-foreground">
                      {entry.rank ?? "-"}
                    </span>
                  </td>
                  <td className="w-24 md:w-32 px-2 md:px-3 py-1.5 border-r border-border-subtle/20">
                    <div className="flex items-center gap-2">
                      {renderTraderAvatar(entry, "h-7 w-7 text-xs")}
                      <span className="text-base md:text-xl font-semibold font-mono text-foreground">
                        {getTraderDisplayName(entry)}
                      </span>
                    </div>
                  </td>
                  <td className="px-2 md:px-3 py-1.5 text-right border-r border-border-subtle/20">
                    <span className={cn(
                      "text-base md:text-xl font-mono",
                      isPassive ? "text-foreground-muted" : entry.unrealized_pnl >= 0 ? "text-success" : "text-danger"
                    )}>
                      {isPassive ? "-" : formatValue(entry.unrealized_pnl)}
                    </span>
                  </td>
                  <td className="px-2 md:px-3 py-1.5 text-right border-r border-border-subtle/20">
                    <span className={cn(
                      "text-base md:text-xl font-mono",
                      isPassive ? "text-foreground-muted" : entry.realized_pnl >= 0 ? "text-success" : "text-danger"
                    )}>
                      {isPassive ? "-" : formatValue(entry.realized_pnl)}
                    </span>
                  </td>
                  <td className="hidden md:table-cell px-3 py-1.5 text-right border-r border-border-subtle/20">
                    <span className={cn(
                      "text-xl font-mono",
                      entry.trade_count > 0 ? "text-foreground" : "text-foreground-muted"
                    )}>
                      {entry.trade_count}
                    </span>
                  </td>
                  <td className="px-2 md:px-3 py-1.5 text-right">
                    <span className={cn(
                      "text-base md:text-xl font-semibold font-mono",
                      isPassive ? "text-foreground-muted" : "text-foreground"
                    )}>
                      {isPassive ? "-" : numberFormatter.format(entry.total_value)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile/Tablet Card View */}
      <div className="standingsMobileFieldList mx-auto w-full max-w-[56rem] space-y-2 px-3 py-3 md:space-y-3 md:px-4">
        {fieldEntries.length > 0 && (
          <div className="grid grid-cols-[2.25rem_minmax(0,1fr)_3.5rem_4.8rem] items-center gap-2 rounded-[14px] border border-border-subtle/60 bg-white/[0.035] px-3 py-2 md:grid-cols-[2.75rem_minmax(14rem,1.7fr)_minmax(4.2rem,0.45fr)_minmax(5.6rem,0.62fr)] md:gap-3">
            <span className="font-display text-[11px] font-semibold uppercase leading-none text-foreground-muted">{t("standings.table.rank")}</span>
            <span className="font-display text-[11px] font-semibold uppercase leading-none text-foreground-muted">{t("standings.table.trader")}</span>
            <span className="text-right font-display text-[11px] font-semibold uppercase leading-none text-foreground-muted">{t("standings.table.trades")}</span>
            <span className="text-right font-display text-[11px] font-semibold uppercase leading-none text-foreground-muted">{t("standings.table.pnl")}</span>
          </div>
        )}
        {fieldEntries.map((entry: LeaderboardEntry) => {
          const isPassive = entry.rank === null || entry.trade_count === 0;
          const isCurrentTrader = isCurrentTraderEntry(entry);

          return (
            <div
              key={getLeaderboardEntryKey(entry)}
              className={cn(
                "standingsMobileFieldCard standingsTabletFieldCard relative overflow-hidden rounded-[18px] border bg-background-card px-3 py-3 shadow-sm transition-all md:px-4 md:py-4",
                isCurrentTrader
                  ? "border-warning/60 bg-warning/10 shadow-warning/10"
                  : "border-border-subtle/60",
                isPassive && "opacity-70",
              )}
            >
              <div
                className={cn(
                  "absolute inset-y-0 left-0 w-0.5",
                  isPassive
                    ? "bg-white/10"
                    : entry.total_pnl > 0
                      ? "bg-success/70"
                      : entry.total_pnl < 0
                        ? "bg-danger/70"
                        : "bg-white/20",
                  )}
                />
              <div className="grid grid-cols-[2.25rem_minmax(0,1fr)_3.5rem_4.8rem] items-center gap-2 md:grid-cols-[2.75rem_minmax(14rem,1.7fr)_minmax(4.2rem,0.45fr)_minmax(5.6rem,0.62fr)] md:gap-3">
                <div
                  className={cn(
                    "standingsTabletRankBadge flex h-8 w-8 items-center justify-center rounded-full border bg-background-surface font-display text-base font-bold leading-none text-foreground md:h-8 md:w-8 md:text-base",
                    isCurrentTrader ? "border-warning/60 text-warning" : "border-border-subtle/70",
                  )}
                >
                  {entry.rank ?? "-"}
                </div>

                <div className="min-w-0">
                  <div className="flex min-w-0 items-center gap-2">
                    {renderTraderAvatar(entry, "standingsTabletTraderAvatar h-10 w-10 text-xs md:h-10 md:w-10 md:text-xs")}
                    <div className="min-w-0">
                      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                        <span className="standingsTabletTraderName min-w-0 truncate font-display text-xl font-bold uppercase leading-none text-foreground md:text-lg">
                          {getTraderDisplayName(entry)}
                        </span>
                        {isCurrentTrader && (
                          <span className="rounded-[6px] border border-warning/50 bg-warning/15 px-1.5 py-0.5 font-display text-[10px] font-bold uppercase leading-none text-warning">
                            {t("standings.you")}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="standingsMobileMetric text-right">
                  <div className="font-display text-[11px] font-semibold uppercase leading-none text-foreground-muted">{t("standings.table.trades")}</div>
                  <div className={cn("mt-1 font-mono text-xl font-bold leading-none tabular-nums", entry.trade_count > 0 ? "text-foreground" : "text-foreground-muted")}>
                    {entry.trade_count}
                  </div>
                </div>

                <div className="standingsMobileMetric text-right">
                  <div className="font-display text-[11px] font-semibold uppercase leading-none text-foreground-muted">{t("standings.table.pnl")}</div>
                  <div className={cn("mt-1 font-mono text-xl font-bold leading-none tabular-nums", getPnlClassName(entry))}>
                    {shouldShowPnl(entry) ? formatPnlOneDecimal(entry.total_pnl) : "-"}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default StandingsPage;
